import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { TokenService, AppInsightsService, FeatureFlagService, genTinyGUID, CookieService, SawDust } from '@cudirect/shared-ui-library';
import { GoogleTrackingService } from '@shared/service/google-tracking.service';
import { LenderService } from '@shared/service/lender/lender.service';
import { L360Service } from '@workflow/ecp-workflow/dynamic-app/service/l360/l360.service';
import { ApplicationApiService } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.service';
import { UserService } from '@workflow/ecp-shared/service/user/user.service';
import { SubscriptionEmitService } from '@workflow/ecp-shared/service/subscription-emit/subscription-emit.service';
import { StorageWrapperService } from '@workflow/ecp-shared/service/storage-wrapper/storage-wrapper.service';
import { processPath } from '@workflow/ecp-workflow/dynamic-app/service/utilities.service';
import { ApiResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { PickListTypes } from '@workflow/ecp-workflow/dynamic-app/types/picklist';
import { Info } from '@workflow/ecp-shared/constant/information.const';
import { ApplicationState, OAOAppStatus } from '@workflow/ecp-shared/enum/enum';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { getFlowInfo, FlowInfo } from '@workflow/helper/flow-utility/flow-utility';
import { LogicFnContext } from '@workflow/service/logic/logic.service';
import { WorkflowWidgetEventServiceService } from '@workflow/widget/workflow-widget-event.service';
import { OlaPageHelper } from '../../helper/ola-page.helper';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { LenderConfiguration } from '@shared/model/lender-config.model';
import { AdminPortalFeaturesService } from '@workflow/ecp-shared/service/user/admin-portal-features.service';
import { BundleHelperService } from '@shared/service/bundle-helper.service';
import { ProgressBarDataService } from '@workflow/dynamic-render/shared/workflow-progress/progress-bar.service';
import { LoanType } from '@shared/enum';
import { StatefulService } from '@app/service/stateful.service';
import { FlowInstance, FlowService } from '@workflow/class/flow.service';
/**
 * Methods on this service get registered as "logic functions" on LogicService
 */
@Injectable({ providedIn: 'root' })
export class OlaOnClickFunctionsService {
  private logger = inject(SawDust).withContext({ component: 'OlaOnClickFunctionsService' });
  /**
   * Lender data configuration
   */
  public get lenderData(): LenderConfiguration {
    // Get the lender data configuration
    return this.lenderService.lenderData;
  }

  constructor(
    public readonly applicationApiService: ApplicationApiService,
    private readonly widgetEventService: WorkflowWidgetEventServiceService,
    private readonly flowHelper: OlaPageHelper,
    private readonly userService: UserService,
    public readonly lenderService: LenderService,
    protected readonly applicationInsightsService: AppInsightsService,
    private readonly router: Router,
    private readonly subscribeEmitService: SubscriptionEmitService,
    private readonly l360Service: L360Service,
    public readonly featureFlagService: FeatureFlagService,
    public readonly storageWrapperService: StorageWrapperService,
    private readonly googleTrackingService: GoogleTrackingService,
    private readonly cookieService: CookieService,
    private readonly adminPortalFeaturesService: AdminPortalFeaturesService,
    private readonly recaptcha: ReCaptchaV3Service,
    private readonly bundleHelperService: BundleHelperService,
    // Importing this as an easy/angular way to get product names
    // for the gtag push events
    private readonly progressBarDataService: ProgressBarDataService,
    private readonly tokenService: TokenService,
    private readonly statefulService: StatefulService,
    private readonly flowService: FlowService
  ) {}

  /**
   * Trigger adding a new card (= new array item)
   * @param _context LogicFnContext
   * @param pathArrayRoot e.g. 'employments' for 'api:employments/$/xyz'
   */
  public addNewCardForAdditionalEmployment = (_context: LogicFnContext, rootPath: string): void => {
    this.statefulService.arrayAdd(processPath(rootPath, _context.page.pageIterationId), () => {
      setTimeout(() => {
        const cardSize = this.statefulService.arraySize(`${rootPath}`, _context.field.pageIterationId) - 1;
        this.statefulService.wrap(`${rootPath}/$/current`, cardSize, _context.page.pageIterationId).value = true;
        this.statefulService.wrap(`${rootPath}/$/payFrequency`, cardSize, _context.page.pageIterationId).value = 'Yearly';
      });
    });
  };

  public olaSaveBundle = (context: LogicFnContext): void => {
    // Only run the below logic when FF is turned OFF
    if (!this.applicationApiService.isNonPartialOLAFeatureFlagEnabled()) {
      if (context.page && context.routeEvent.url !== '/user/login') {
        // Trigger a partial save
        this.applicationApiService.partialApplication = context.page.allowSavePartialApp && context.page.helper.isPageComplete;

        // Add a generateAppId URL params to the applicationApiService
        this.applicationApiService.generateAppId = context.page.generateAppId;
        // Using the page.id to clear out the Session Storage
        if (this.featureFlagService.isFeatureEnabled('SaveAppDataToSessionStorage')) {
          this.applicationApiService.updateBundleDataToDDMS(
            LoanType.OLA.toLowerCase(),
            null,
            null,
            context.page.id.toString() === 'ola-application-review'
          );
        } else {
          this.applicationApiService.updateBundleDataToDDMS(LoanType.OLA.toLowerCase());
        }
      }
    }
  };

  public setOLAFlow = (_context: LogicFnContext): boolean => {
    this.statefulService.wrap('ss:ola-fresh-loan').value = true;
    this.statefulService.wrap('ola:olaApplicationInformation/isEligibilityCompleted').value = true;
    this.goToIncompletePage(_context);
    return true;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public flowInitializer = (context: LogicFnContext): void => {
    this.flowHelper.olaFlowInitializer();
  };

  public checkLoanTypeFlow = (context: LogicFnContext): void => {
    const flow = context.flow;
    if (this.flowHelper.checkLoanTypeChange()) {
      this.statefulService.wrap('ss:showLoanGroupChangeModal').value = true;
      return;
    }
    const prevLoanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value;
    const prevLoanType = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value;
    const loanGroup = this.statefulService.wrap('ss:ola-new-loan-group').valueString;
    const loanProduct = this.statefulService.wrap('ss:ola-new-loan-type').valueString;

    if (flow.id === 'ola' && !prevLoanGroup && !prevLoanType) {
      const newUITemplate = this.statefulService.wrap('ss:ola-new-ui-template').valueString;

      this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value = loanGroup;
      this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').value = newUITemplate;
      this.statefulService.wrap('ola:olaApplicationInformation/showAdditionalQuestions').value = true;

      let loanGroups = [];
      if (this.adminPortalFeaturesService?.config?.olaConfiguration) {
        loanGroups = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups;
      }
      const selectedLoanGroup = loanGroups.filter((t) => t.loanGroup === loanGroup)[0];
      // Load Loan Purpose dropdown
      this.loadLoanPurposeDropdown(selectedLoanGroup, loanProduct);
      // Patching the LoanProductName in case of a single LoanProduct as well.
      // It is necessary for retreival of Post App Notification Messages from the Lender Config in arc DX
      if (selectedLoanGroup.isLoanGroupHasProduct || selectedLoanGroup.loanProducts.length === 1) {
        this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value = loanProduct;
        this.statefulService.wrap('ss:is-loangroup-got-changed-for-make').value = true;
      } else {
        this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').value = null;
      }
    }

    this.statefulService.wrap('ss:ola-new-loan-group').value = '';
    this.statefulService.wrap('ss:ola-new-loan-type').value = '';
    this.statefulService.wrap('ss:ola-new-ui-template').value = '';
    this.statefulService.wrap('ss:ola-fresh-loan').value = true;
    this.userService.flowType = 'OLA';

    this.handlePageSaveAndNavigate(flow, context);
    context.defaultNavigationFn(); // updating page state here
  };

  /**
   * Load Loan Purpose Dropdown
   * @param loanGroup
   * @param loanProduct
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private loadLoanPurposeDropdown(loanGroup: any, loanProduct: string): void {
    const selectedpickListType = 'LoanPurpose';
    const lenderLoanProduct = loanGroup.loanProducts.filter(
      (t) => !loanGroup.isLoanGroupHasProduct || (loanProduct && t?.productName?.toLowerCase() === loanProduct?.toLowerCase())
    )[0]?.lenderLoanProduct;

    if (this.l360Service.getPickListTypes(selectedpickListType, lenderLoanProduct).length === 0) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      this.l360Service.picklistLoading['LoanPurpose'] = true;
      this.l360Service.getPickListItems(selectedpickListType, lenderLoanProduct).subscribe((data: ApiResult<PickListTypes>) => {
        if (data.isSuccess) {
          // set LoanPurposePickListData
          this.l360Service.LoanPurposePickListData = data?.data;
        }
        // eslint-disable-next-line @typescript-eslint/dot-notation
        this.l360Service.picklistLoading['LoanPurpose'] = false;
      });
    }
  }

  /**
   * This method traverse to incomplete OLA page
   * @param context
   */
  public goToIncompletePage = (context: LogicFnContext): void => {
    const flow = context.flow;
    const extraData = this.statefulService.wrap('ola:extraData').valueString;
    let extraDataObj;
    try {
      extraDataObj = JSON.parse(extraData);
    } catch (error) {
      extraDataObj = undefined;
    }

    const incompletedPages = flow.pages.filter(
      (t) => (t.visible && !t.helper.isPageComplete) || !this.isExtradataCompleted(t.id, extraDataObj)
    );
    if (incompletedPages && incompletedPages.length > 0 && incompletedPages[0]) {
      this.statefulService.wrap('ss:oao-first-time-navigation').value = false;
    }

    if (flow.id === 'ola' && incompletedPages.length === 0) {
      this.routeFromBundle();
      return;
    }
    this.handlePageSaveAndNavigate(flow, context);
  };

  // eslint-disable-next-line @typescript-eslint/ban-types
  private isExtradataCompleted(id: string, extraDataObj: object): boolean {
    if (!extraDataObj) {
      return false;
    }

    return !(!extraDataObj[id]?.isVisited || extraDataObj[id]?.state?.toLowerCase() !== 'completed');
  }

  private routeFromBundle() {
    const selectedTemplateName = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
    this.storageWrapperService.set(Info.allowedRoute, 'Application');
    const templates = this.adminPortalFeaturesService?.config?.olaConfiguration?.masterUITemplates;
    const template = templates.filter((t) => t.templateName.toLowerCase() === selectedTemplateName?.toLowerCase())[0];

    if (template) {
      const flow = this.flowService.createFlow(template.path);
      const flowInfo = getFlowInfo(flow);
      const path = this.getOLAFirstVisiblePage(flow, flowInfo, template.path);
      this.router.navigate([`app/` + path]);
    } else {
      this.statefulService.wrap('ss:is-app-has-no-template').value = true;
      this.router.navigate(['app/ola/ola-consumer-loan-type']);
    }
  }

  // Sets a cookie to indicate the authentication type used for the application. This is read later
  // in arcDX and fed into Login Widget.
  private handleAuthTypeCookie(context: LogicFnContext): void {
    const phoneType = this.statefulService.wrap('ola:borrowerInfo/phoneType').value;
    const isPersonalInfo = context.page.id.includes('personal-info');
    const isUserLoggedIn = this.userService.isAuthenticated;
    if (phoneType === 'Mobile' && isPersonalInfo && !isUserLoggedIn) {
      this.cookieService.setCookie('arcDXAuthType', 'phone', 365, '/');
      this.cookieService.setCookie('arcDXReturningUser', 'true', 90, '/');
    } else if (phoneType === 'HomePhone' && isPersonalInfo && !isUserLoggedIn) {
      this.cookieService.setCookie('arcDXAuthType', 'email', 365, '/');
      this.cookieService.setCookie('arcDXReturningUser', 'true', 90, '/');
    }
  }

  private redirectToLoanDetails(): void {
    this.statefulService.wrap('ss:is-app-has-no-template').value = false;
    const uiTemplate = this.statefulService.wrap('ola:olaApplicationInformation/uiTemplateName').valueString;
    let templates = [];
    if (this.adminPortalFeaturesService?.config?.olaConfiguration) {
      templates = this.adminPortalFeaturesService?.config?.olaConfiguration?.masterUITemplates;
    }
    const template = templates.filter((t) => t.templateName.toLowerCase() === uiTemplate.toLowerCase())[0];
    const flow = this.flowService.createFlow(template.path);
    const flowInfo = getFlowInfo(flow);
    const path = this.getOLAFirstVisiblePage(flow, flowInfo, template.path);
    this.router.navigate(['app/' + path]);
  }

  private getOLAFirstVisiblePage(flow: FlowInstance, flowInfo: FlowInfo, workflowPrefix: string): string {
    let pageToRedirect = 'ola/ola-consumer-loan-type';
    if (flow.id.toLowerCase() === workflowPrefix) {
      const visiblePages = flow.pages.filter((t) => t.visible && t.id && t.id !== 'ola-consumer-loan-type');
      if (visiblePages && visiblePages.length > 0) {
        pageToRedirect = `${flowInfo.configPath}/${visiblePages[0].path}`;
      }
    }
    return pageToRedirect;
  }

  /**
   * Submits an Online Loan Application
   *
   * The method first checks if the page is complete using the `context.page.helper.isPageComplete`.
   * If the page is not complete, the method returns early. If the page is complete, it initiates the
   * process to submit the OLA.
   *
   * The method also logs certain application insights events (start and end of OLA submission) and
   * has provisions for handling success and error scenarios.
   *
   * @remarks
   * There are certain TODOs noted within the method:
   * 1. Replace `genTinyGUID` with `crypto.randomUUID` in future TypeScript updates.
   * 2. Add coborrower information to log metadata.
   * 3. Investigate the `appApiService.SubmitOLA` implementation about its success handling.
   *
   * @param context - An object containing properties related to the current logic function context,
   * including the page's helper object and other relevant information.
   *
   * @example
   * ```typescript
   * const context: LogicFnContext = {
   *    page: { helper: { submitStatus: undefined, isPageComplete: true }, actionButtonIsLoading: false }
   * };
   * submitOlaApplication(context);
   * ```
   */
  public submitOlaApplication = (context: LogicFnContext): void => {
    context.page.helper.submitStatus = undefined;
    if (!context.page.helper.isPageComplete) {
      return;
    }
    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    // Set the loader to true and we don't want to set it to false.
    // Once the user clicks submit we should not enable the button again unless the user refreshes the page.
    context.page.actionButtonIsLoading = true;
    this.statefulService.saveChangedValues();
    // Using one of the common ID's from the bundle to make a GET call
    this.statefulService.wrap('ola:borrowerInfo/firstName').load(() => {
      this.applicationApiService.submitResult = false;

      // Logging variables
      const logUser = this.statefulService.wrap('ola:borrowerInfo/emailAddress').valueStringOnly ?? 'Unknown';
      const logLoanNumber = this.statefulService.wrap('ola:borrowerInfo/applicationID').valueStringOnly ?? 'Unknown';

      // TODO: Add coborrower info to log metadata
      this.logAppInsightsOLASubmitEvent(eventPairId, 'start', logLoanNumber, logUser);
      if (this.applicationApiService.isNonPartialOLAFeatureFlagEnabled()) {
        this.applicationApiService.updateBundleDataToDDMS(
          LoanType.OLA.toLowerCase(),
          (statusCode) => {
            if (statusCode === HttpStatusCode.Ok) {
              this.processOLASubmit(context, eventPairId, logLoanNumber, logUser);
            }
          },
          (statusCode) => {
            if (statusCode === HttpStatusCode.Conflict) {
              context.page.isAppMigratedToOS = true;
              this.statefulService.wrap('ss:showMigratedApplicationModal').value = true;
              return;
            }
            context.page.saveIsFailed = true;
          },
          null
        );
      } else {
        this.applicationApiService.patchChangedValues('ola', (isSuccess, error) => {
          if (isSuccess) {
            this.logger.info('Patch values are saved successfully on OLA submit.');
          } else {
            this.logger.withError(error).warn('Unable to save OLA patch value on submit.');
          }
          this.processOLASubmit(context, eventPairId, logLoanNumber, logUser);
        });
      }
      this.handleAfterSubmission();
    });
  };

  /** Method is responsible for setting up the Session Storage with data needed to load PostApp & emit application submit event */
  private handleAfterSubmission(): void {
    this.statefulService.wrap('ss:ola-new-loan-submitted').value = true;
    this.setUserForPostApp();
    const loanNumber = this.statefulService.wrap('ola:borrowerInfo/applicationID').valueStringOnly;
    this.widgetEventService.emitApplicationSubmit({ loanNumber, flowId: 'ola' });
  }

  private processOLASubmit(context: LogicFnContext, eventPairId: string, logLoanNumber: string, logUser: string): void {
    this.applicationApiService.submitOLAApplication(
      (isSuccess) => {
        // Indicate that the action button is no longer loading
        context.page.actionButtonIsLoading = false;

        this.statefulService.wrap('ola:borrowerInfo/applicationID').load(() => {
          context.page.helper.submitStatus = true;
        });
        this.subscribeEmitService.emit(Info.oaoSubmitListener, isSuccess ? Info.submitted : Info.failed);

        // TODO: Add coborrower info to log metadata
        // NOTE: Per implementation of appApiService.SubmitOLA, isSuccess likely always true, otherwise err cb is triggered
        // NOTE: Need to investigate this
        this.logAppInsightsOLASubmitEvent(
          eventPairId,
          'end',
          logLoanNumber,
          logUser,
          isSuccess ? 'success' : 'error',
          isSuccess ? null : 'Unknown error (see NOTES in on-click-functions.service.ts)'
        );
        if (this.applicationApiService.isNonPartialOLAFeatureFlagEnabled()) {
          this.handleAfterSubmission();
        }

        const applicationId = this.storageWrapperService.get<string>(Info.appNumber);
        // Added entity id for tracking in arcDX App IDs. Will replace arc OS App Id in future.
        const entityId = this.statefulService.wrap('api:entityId').value;
        this.googleTrackingService.publishEvent('SubmitOLAApplication', {
          ApplicationId: applicationId,
          ArcDXApplicationId: entityId,
          LoanProductGroup: this.progressBarDataService?.productName,
          LoanProductName: this.progressBarDataService?.loanProductName,
          LoanPurpose: this.progressBarDataService?.loanPurpose
        });
        this.googleTrackingService.publishEvent('arcdx.loans.submit', {
          ApplicationId: applicationId,
          ArcDXApplicationId: entityId,
          ProductType: this.progressBarDataService?.productName,
          ProductSubType: this.progressBarDataService?.loanProductName,
          LoanPurpose: this.progressBarDataService?.loanPurpose
        });
      },
      (err: HttpErrorResponse) => {
        context.page.helper.submitStatus = false;
        this.subscribeEmitService.emit(Info.oaoSubmitListener, Info.failed);

        // TODO: Add coborrower info to log metadata and exception metadata (218667)
        this.logAppInsightsOLASubmitEvent(eventPairId, 'end', logLoanNumber, logUser, 'error', JSON.stringify(err?.error));
      }
    );
  }

  /**
   * Centralized logging method to handle both ApplicationInsights and SawDust logging
   * @param eventName The name of the event to log
   * @param eventData The data to include with the event
   */
  private logEvent(eventName: string, eventData: Record<string, unknown>): void {
    this.applicationInsightsService.logEvent(eventName, eventData);
    this.logger.logEvent(eventName, eventData);
  }

  /**
   * Logs an event in Application Insights, typically called when a loan submission process either starts or ends.
   * This method is specifically meant for tracking the status and associated details of each loan submission attempt.
   *
   * @param correlationId - Unique identifier to correlate events in a single transaction.
   * @param eventState - Represents the state of the event, whether it's 'start' or 'end'.
   * @param loanNumber - Unique identifier of the loan which is being processed.
   * @param user - Identifier for the user triggering this event, could be username or user ID.
   * @param status - Optional parameter that captures the status of the event. It can either be 'success' or 'error'.
   * @param errorDetails - Optional parameter that captures the details of the error, if any.
   *
   * @returns This method doesn't have a return.
   */
  private logAppInsightsOLASubmitEvent(
    correlationId: string,
    eventState: 'start' | 'end',
    appNumber: string,
    user: string,
    status: 'success' | 'error' = null,
    errorDetails: string = null
  ): void {
    const source = 'workflow'; // we also track this event in the arcDX UI for Post-App resubmit
    const domain = window.location.origin; // TODO: SHOULD BE DEFAULT IN ALL LOGS
    this.logEvent('ecp.ui.olaSubmit', {
      eventState,
      correlationId,
      status,
      appNumber,
      user,
      domain,
      source,
      errorDetails
    });
  }

  private setUserForPostApp() {
    const userInfo = this.userService.user;
    const firstName = this.statefulService.wrap('ola:borrowerInfo/firstName').valueString;
    const lastName = this.statefulService.wrap('ola:borrowerInfo/lastName').valueString;
    userInfo.userName = userInfo.firstName = firstName;
    userInfo.lastName = lastName;
    this.userService.user = userInfo;
    this.statefulService.wrap(`ola:firstTimeLogin`).value = false;
    // eslint-disable-next-line eqeqeq
    this.userService.appNumber = '';
    this.userService.allowedRoutes = 'Consumer_PostApp';
    this.userService.isConsumer = true;
    this.applicationApiService.submitResult = false;
    this.userService.flowType = 'OLA';
    this.userService.loanStatus = ApplicationState.StatusInProgress;
    this.userService.statusMessage = `Hi ${firstName}, we're reviewing your application now.`;
    this.userService.sourceLoanType = this.statefulService.wrap('ola:olaApplicationInformation/loanTypeName').valueString;
    this.userService.appStatus = OAOAppStatus.Submitted;
    this.userService.applicants = `${firstName} ${lastName}`;
    this.userService.loanProductName = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    this.userService.loanGroupIcon = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupIcon').valueString;
    this.userService.loanProductDisplayName = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    this.userService.selectedLoanProduct = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    this.userService.loanType = this.statefulService.wrap('ola:olaApplicationInformation/loanTypeName').valueString;
    this.userService.requestedLoanAmount = this.statefulService.wrap(
      'ola:olaApplicationInformation/financing/requestedLoanAmount'
    ).valueString;
    this.userService.decision_amount = null;
    this.userService.decision_rate = null;
    this.userService.decision_term = this.statefulService.wrap('ola:olaApplicationInformation/financing/loanTerm').valueString;
    this.userService.decision_estMonthlyPayment = this.statefulService.wrap('ola:olaApplicationInformation/monthlyPayment').valueString;
    this.generatePostAppMessages();
    // For Non Partial OLA Apps, since we do not have the App Number available on Submission,
    // Using the stored appNumber until we get it back from the API
    const loanNumber = this.applicationApiService.isNonPartialOLAFeatureFlagEnabled()
      ? this.storageWrapperService.get<string>(Info.appNumber)
      : this.statefulService.wrap('ola:borrowerInfo/applicationID').value;
    this.storageWrapperService.set(Info.appNumber, loanNumber);
  }

  /**
   * Method to generate the Post App Messages and store in the session
   */
  public generatePostAppMessages() {
    const loanProductName = this.statefulService.wrap('ola:olaApplicationInformation/loanProductName').valueString;
    let loanGroups = [];
    if (this.adminPortalFeaturesService?.config?.olaConfiguration) {
      loanGroups = this.adminPortalFeaturesService?.config?.olaConfiguration?.consumerLoanGroups;
    }
    const loanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').value;
    const selectedLoanGroup = loanGroups ? loanGroups.filter((t) => t.loanGroup === loanGroup)[0] : null;
    const selectedLoanProduct = selectedLoanGroup
      ? selectedLoanGroup.loanProducts.filter((f) => f.productName === loanProductName)[0]
      : null;
    const productTemplateId = selectedLoanProduct ? selectedLoanProduct.postAppNotificationTemplateId : null;

    let postAppNotificationTemplates = [];
    if (this.adminPortalFeaturesService?.config?.olaConfiguration) {
      postAppNotificationTemplates = this.adminPortalFeaturesService?.config?.olaConfiguration?.postAppNotificationTemplates;
    }

    if (!postAppNotificationTemplates) {
      return;
    }

    const defaultTemplate = postAppNotificationTemplates.filter((f) => f.isDefault === true)[0];
    let selectedProductTemplate = productTemplateId
      ? postAppNotificationTemplates.filter((f) => f.id === productTemplateId && f.isEnabled)[0]
      : null;

    if (!defaultTemplate) {
      return;
    }

    if (!selectedProductTemplate) {
      selectedProductTemplate = defaultTemplate;
    }

    // Status message
    if (selectedProductTemplate.stages?.pending?.status?.useDefault) {
      this.userService.postAppStatusMessage = defaultTemplate.stages?.pending?.status?.message;
    } else {
      this.userService.postAppStatusMessage = selectedProductTemplate.stages?.pending?.status?.message;
    }
    // Notification flag & message
    if (selectedProductTemplate.stages?.pending?.notification?.useDefault) {
      this.userService.isPostAppNotificationActive = defaultTemplate.stages?.pending?.notification?.isEnabled;
      this.userService.postAppNotificationMessage = defaultTemplate.stages?.pending?.notification?.message;
    } else {
      this.userService.isPostAppNotificationActive = selectedProductTemplate.stages?.pending?.notification?.isEnabled;
      this.userService.postAppNotificationMessage = selectedProductTemplate.stages?.pending?.notification?.message;
    }
  }

  public validateDraftApplication = (context: LogicFnContext): void => {
    if (context.page.allowSavePartialApp) {
      this.l360Service.validateDraftApp();
    }
  };
  public validateOLADraftApplication = (): void => {
    const selectedloanGroup = this.statefulService.wrap('ola:olaApplicationInformation/loanGroupName').valueString;

    // Bug #244854 check loangroup is exist, before making validate draft call.
    if (selectedloanGroup && this.flowHelper?.previousPage?.helper?.isPageComplete === true) {
      this.l360Service.validateOLADraftApp();
    }
  };

  /**
   * This method used to regsiter the user and next to incomplete OAO page
   * @param context
   */
  public saveOLAPersonalInfo = (context: LogicFnContext): void => {
    this.handleAuthTypeCookie(context);

    /** check whether the user already registered */
    const isRegisteredApplication = this.statefulService.wrap('ola:isRegisteredApplication').value;
    const isAuthenticated = this.userService.isAuthenticated;

    if (isRegisteredApplication || isAuthenticated) {
      const cachedToken = this.tokenService.getCachedToken();
      let tokenType = 'unknown';
      if (cachedToken) {
        tokenType = this.tokenService.determineTokenType();
      }

      if (cachedToken && tokenType === 'arcDX') {
        this.registerOrRefreshBundle(context, true);
      } else {
        this.refreshBundleAndGoToIncompletePage(context);
      }
    } else {
      this.registerOrRefreshBundle(context);
    }
  };

  /**
   * Registers the user or refreshes the bundle if the user is already registered.
   *
   * This method handles the user registration or bundle refresh logic based on the current context.
   * If the user is not registered, it constructs the registration payload and invokes the silent registration process.
   *
   * @param context - The current logic function context containing the page and other runtime
 information.
   *
   * @remarks
   * - If the user is already registered, it directly refreshes the bundle.
   * - Ensures compatibility with reCAPTCHA for security during registration.
   * - Invokes the `olaSilentRegisterWithRecaptcha` for user registration when needed.
   */
  private registerOrRefreshBundle(context: LogicFnContext, isInternal = false): void {
    const registerPayload = this.getRegisterPayload();
    this.olaSilentRegisterWithRecaptcha(registerPayload, context, isInternal);
  }

  /**
   * Refreshes the bundle and navigates to the first incomplete OLA page.
   *
   * This method ensures that the application bundle is up-to-date with the latest
   * data, including changes such as the addition of co-borrowers. After the refresh,
   * it redirects the user to the first incomplete page of the application flow.
   *
   * @param context - The current logic function context containing flow and page details.
   *
   * @remarks
   * - Clears a specific preview state (`mem:edit-ola-personal-preview`) after navigation.
   * - Should be invoked after updating bundle data to maintain consistency.
   */
  private refreshBundleAndGoToIncompletePage(context: LogicFnContext): void {
    /** Refreshing the bundle to get updated coborrower array during automation */
    this.bundleHelperService.refreshBundle(() => {
      this.goToIncompletePage(context);
      this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
    });
  }

  public getRegisterPayload() {
    const email = this.statefulService.wrap('ola:borrowerInfo/emailAddress').valueStringOnly?.toLowerCase();
    const cellPhone = this.statefulService.wrap('ola:borrowerInfo/primaryPhoneNumber').valueStringOnly;
    const phoneType = this.statefulService.wrap('ola:borrowerInfo/phoneType').valueStringOnly;
    const formattedCellPhone = cellPhone ? cellPhone.replace(/\D/g, '') : cellPhone;
    return {
      email,
      confirmEmail: email,
      isRegisteredWithCuid: false,
      registeredWithSSO: false,
      loginType: 'OLA',
      cellPhone: phoneType === 'Mobile' ? formattedCellPhone : '',
      firstName: this.statefulService.wrap('ola:borrowerInfo/firstName').valueString,
      lastName: this.statefulService.wrap('ola:borrowerInfo/lastName').valueString,
      isSilentRegistration: true,
      dateOfBirth: this.statefulService.wrap('ola:borrowerInfo/dateOfBirth').valueString
    };
  }

  /**
   * This method used to regsiter the user with recaptcha verification and next to incomplete page
   * Prefixed this will ola as to not clash with a near identical one in oao-on-click-functions
   * We need to dedupe and consolidate onclick functions into 1 file
   * @param context
   */
  private olaSilentRegisterWithRecaptcha = (postData, context: LogicFnContext, isInternal = false): void => {
    if (
      this.lenderData.enableRecaptcha &&
      this.statefulService.wrap(`ola:borrowerInfo/autoFillOption`).valueString !== 'Prefill' &&
      !isInternal
    ) {
      this.recaptcha.execute('silent_register').subscribe({
        next: (token) => {
          this.olaSilentRegister(postData, context, token);
        },
        error: () => {
          this.statefulService.wrap('ss:showMFAVerification').value = true;
        }
      });
    } else {
      this.olaSilentRegister(postData, context);
    }
  };

  /**
   * This method used to regsiter the user and next to incomplete OAO page
   *    * Prefixed this will ola as to not clash with a near identical one in oao-on-click-functions
   * We need to dedupe and consolidate onclick functions into 1 file
   * @param context
   */
  private olaSilentRegister = (postData, context: LogicFnContext, token?: string): void => {
    const user = this.userService.user || ({} as UserInfo);
    user.cellPhone = postData.cellPhone;
    user.email = postData.email;
    user.mfaVerifyMethod = user.cellPhone ? 'Phone' : 'Email_Code';
    this.userService.user = user;
    this.applicationApiService.storagePrefix = 'ola';
    this.applicationApiService.silentRegistration(
      postData,
      token,
      (response) => {
        if (!response.isSuccess) {
          this.statefulService.wrap('ss:showMFAVerification').value = true;
          return;
        }
        this.userService.isExistingUser = true;

        if (!!token) {
          this.statefulService.wrap('ola:isRecaptchaVerified').value = true;
        }
        this.statefulService.wrap('ola:isRegisteredApplication').value = true;
        this.statefulService.wrap('ss:trigger-appId-creation-event').value = true; // Triggers GA event once appId available
        // this.SetUser(response);
        /** Refreshing the bundle to get updated coborrower array during automation */
        this.bundleHelperService.refreshBundle(() => {
          this.goToIncompletePage(context);
          this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
        });
      },
      () => {
        this.statefulService.wrap('mem:edit-ola-personal-preview').value = false;
      }
    );
  };

  /**
   * Method to handle Page level Save and Navigation
   * @param flow - current flow type: OLA or OAO
   * @param context - current context
   */
  public handlePageSaveAndNavigate(flow: FlowInstance, context: LogicFnContext): void {
    if (flow.id.includes(LoanType.OLA.toLowerCase()) && this.applicationApiService.isNonPartialOLAFeatureFlagEnabled()) {
      this.processPageSaveAndNavigate(context);
    } else {
      this.doNavigate(context);
    }
  }

  /**
   * Method to process page Save and navigate
   * @param context - current context
   */
  private processPageSaveAndNavigate(context: LogicFnContext): void {
    if (context.page && this.router.url !== '/user/login') {
      this.applicationApiService.updateBundleDataToDDMS(
        LoanType.OLA.toLowerCase(),
        (statusCode) => {
          if (statusCode === HttpStatusCode.Ok) {
            this.doNavigate(context);
          }
        },
        (statusCode) => {
          if (statusCode === HttpStatusCode.Conflict) {
            context.page.isAppMigratedToOS = true;
            // Below conditional check to continue navigation for pages which doesn't contain the customModalType: migrated-application
            // (Ex: Loan Type, Eligibility, Loan Details, Primary Applicant Personal Info)
            if (context.page.config?.customModalType && context.page.config?.customModalType !== 'migrated-application') {
              this.doNavigate(context);
            }
            // Actual trigger to show the Migrated Application Modal in the Page
            this.statefulService.wrap('ss:showMigratedApplicationModal').value = true;
            return;
          }
          context.page.saveIsFailed = true;
        },
        null
      );
    }
  }

  /**
   * Method to handle navigation
   * @param context - current context
   */
  private doNavigate(context: LogicFnContext): void {
    context.defaultNavigationFn(); // updating page state here
    if (!context.page.helper.hasNextPage) {
      this.redirectToLoanDetails();
    }
  }
}
